import React, { useState, useEffect } from 'react';
import { ChevronDown, Clock, Smile } from 'lucide-react';
import { getAllFeedback } from '../../services/roomServices';
import { toast } from 'react-toastify';

export default function FeedbackTab() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await getAllFeedback();
      if (response.success) {
        setFeedbacks(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZoneName: 'short',
    });
  };

  // Rating badge colors
  const getRatingBadge = (rating) => {
    const colors = {
      'Very happy': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Happy': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Neutral': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Sad': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Very sad': 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[rating] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Quality badge colors
  const getQualityBadge = (quality) => {
    const colors = {
      'Excellent': 'text-green-400',
      'Good': 'text-emerald-400',
      'Average': 'text-yellow-400',
      'Poor': 'text-orange-400',
      'Very Poor': 'text-red-400',
    };
    return colors[quality] || 'text-gray-400';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading feedback...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Meeting Feedback</h1>
        <p className="text-gray-400 text-sm mt-1">
          {feedbacks.length} {feedbacks.length === 1 ? 'response' : 'responses'}
        </p>
      </div>

      {/* Feedback Table */}
      {feedbacks.length === 0 ? (
        <div className="bg-white/5 rounded-xl p-12 text-center border border-white/10">
          <Smile className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No feedback found</p>
          <p className="text-gray-500 text-sm mt-2">
            Feedback will appear here when participants submit reviews after meetings
          </p>
        </div>
      ) : (
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          {/* Table Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-white/5 border-b border-white/10 text-xs font-medium text-gray-400 uppercase tracking-wider">
            <div className="col-span-2">Participant</div>
            <div className="col-span-2">Meeting</div>
            <div className="col-span-2">Experience</div>
            <div className="col-span-1">Audio</div>
            <div className="col-span-1">Video</div>
            <div className="col-span-2">Feedback</div>
            <div className="col-span-2">Date</div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-white/5">
            {feedbacks.map((fb) => (
              <div
                key={fb._id}
                onClick={() => setSelectedFeedback(selectedFeedback === fb._id ? null : fb._id)}
                className="hover:bg-white/5 cursor-pointer transition-colors"
              >
                {/* Desktop Row */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-4 items-center">
                  <div className="col-span-2 text-white font-medium text-sm truncate">
                    {fb.feedBackBy || 'Anonymous'}
                  </div>
                  <div className="col-span-2 text-gray-400 text-sm truncate">
                    {fb.meetingName || fb.roomId}
                  </div>
                  <div className="col-span-2">
                    {fb.meetingExperience ? (
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${getRatingBadge(fb.meetingExperience)}`}>
                        {fb.meetingExperience}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-xs">-</span>
                    )}
                  </div>
                  <div className={`col-span-1 text-sm ${getQualityBadge(fb.audioQuality)}`}>
                    {fb.audioQuality || '-'}
                  </div>
                  <div className={`col-span-1 text-sm ${getQualityBadge(fb.videoQuality)}`}>
                    {fb.videoQuality || '-'}
                  </div>
                  <div className="col-span-2 text-gray-400 text-sm truncate">
                    {fb.feedback || <span className="text-gray-600 italic">No comment</span>}
                  </div>
                  <div className="col-span-2 flex items-center justify-between">
                    <span className="text-gray-400 text-sm">{formatDate(fb.createdAt)}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${selectedFeedback === fb._id ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* Mobile Card */}
                <div className="md:hidden px-4 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-medium text-sm">{fb.feedBackBy || 'Anonymous'}</h3>
                    {fb.meetingExperience && (
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${getRatingBadge(fb.meetingExperience)}`}>
                        {fb.meetingExperience}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                    <div>Meeting: {fb.meetingName || fb.roomId}</div>
                    <div>Audio: <span className={getQualityBadge(fb.audioQuality)}>{fb.audioQuality || '-'}</span></div>
                    <div>Video: <span className={getQualityBadge(fb.videoQuality)}>{fb.videoQuality || '-'}</span></div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(fb.createdAt)}
                    </div>
                  </div>
                  {fb.feedback && (
                    <p className="text-gray-400 text-xs mt-2 truncate">{fb.feedback}</p>
                  )}
                </div>

                {/* Expanded Details */}
                {selectedFeedback === fb._id && (
                  <div className="px-5 pb-4 border-t border-white/5">
                    <div className="bg-white/5 rounded-lg p-4 mt-3">
                      <h4 className="text-sm font-medium text-white mb-3">Feedback Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Participant:</span>
                          <span className="text-gray-300">{fb.feedBackBy || '-'}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Meeting:</span>
                          <span className="text-gray-300">{fb.meetingName || fb.roomId}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Room ID:</span>
                          <span className="text-gray-300">{fb.roomId}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Session ID:</span>
                          <span className="text-gray-300">{fb.sessionId || '-'}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Experience:</span>
                          <span className="text-gray-300">{fb.meetingExperience || '-'}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Audio Quality:</span>
                          <span className={getQualityBadge(fb.audioQuality)}>{fb.audioQuality || '-'}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Video Quality:</span>
                          <span className={getQualityBadge(fb.videoQuality)}>{fb.videoQuality || '-'}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-500 min-w-[120px]">Submitted:</span>
                          <span className="text-gray-300">{formatFullDate(fb.createdAt)}</span>
                        </div>
                      </div>
                      {fb.feedback && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <span className="text-gray-500 text-xs">Comment:</span>
                          <p className="text-gray-300 text-sm mt-1">{fb.feedback}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
